import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, GraduationCap, Settings2, RefreshCw, Award, CheckCircle2, Lock, Search } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminAchievements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [reqConfig, setReqConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStudents = () => api.get("/admin/students").then((r) => setStudents(r.data.students)).catch(() => {});
  useEffect(() => { loadStudents(); api.get("/admin/program/required-labs").then((r) => setReqConfig(r.data)).catch(() => {}); }, []);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const openDetail = async (s) => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/students/${s.id}/achievements`); setDetail({ ...data, id: s.id }); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleReq = (id) => setReqConfig((c) => ({ ...c, required: c.required.includes(id) ? c.required.filter((x) => x !== id) : [...c.required, id] }));
  const saveReq = async () => { try { await api.put("/admin/program/required-labs", { required_labs: reqConfig.required }); toast.success("Required labs updated"); setShowConfig(false); loadStudents(); } catch (e) { toast.error(apiErr(e)); } };

  const reissueBadge = async (labId) => { try { const { data } = await api.post(`/admin/students/${detail.id}/reissue-badge`, { lab_id: labId }); toast.success("Badge reissued: " + data.badge.badge_id); openDetail({ id: detail.id }); } catch (e) { toast.error(apiErr(e)); } };
  const reissueCert = async () => { try { const { data } = await api.post(`/admin/students/${detail.id}/reissue-certificate`); toast.success("Certificate reissued: " + data.certificate.certificate_id); openDetail({ id: detail.id }); } catch (e) { toast.error(apiErr(e)); } };

  const filtered = students.filter((s) => !q || (s.name + s.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="admin-achievements-page">
      <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="aa-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Admin Portal
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-6 w-6 text-primary" />
          <h1 className="font-display font-bold text-3xl tracking-tight">Program & Achievements</h1>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => setShowConfig(true)} data-testid="configure-required-labs-btn"><Settings2 className="h-4 w-4" /> Configure Required Labs</Button>
      </div>
      <p className="text-muted-foreground mt-1">Track lab completion, badges earned, and certificate eligibility. Reissue when needed.</p>

      <div className="relative mt-6 max-w-sm">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input className="pl-9" placeholder="Search students…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="aa-search" />
      </div>

      <div className="rounded-lg border border-border bg-card mt-4 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-secondary/50 text-left"><th className="p-3">Student</th><th className="p-3">Labs</th><th className="p-3">Badges</th><th className="p-3">Certificate</th><th className="p-3 text-right">Verify</th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No students.</td></tr>}
            {filtered.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`aa-row-${s.id}`}>
                <td className="p-3"><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.email}</div></td>
                <td className="p-3 font-mono">{s.completed_count}/{s.total_labs}</td>
                <td className="p-3 font-mono">{s.badges_earned}</td>
                <td className="p-3"><Badge variant="outline" className={s.certificate_status === "Awarded" ? "text-success border-success/40" : "text-muted-foreground"}>{s.certificate_status || "Locked"}</Badge></td>
                <td className="p-3 text-right"><Button variant="ghost" size="sm" onClick={() => openDetail(s)} disabled={busy} data-testid={`aa-view-${s.id}`}>View records</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Configure required labs */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent data-testid="required-labs-dialog">
          <DialogHeader><DialogTitle className="font-display">Required Labs for Program Completion</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-auto space-y-1">
            {reqConfig?.labs.map((l) => (
              <label key={l.id} className="flex items-center gap-3 p-2 rounded hover:bg-accent cursor-pointer text-sm" data-testid={`req-lab-${l.id}`}>
                <input type="checkbox" checked={reqConfig.required.includes(l.id)} onChange={() => toggleReq(l.id)} />
                <span className="text-xs text-muted-foreground w-12">L{l.level}</span>
                <span>{l.title}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancel</Button>
            <Button onClick={saveReq} data-testid="save-required-labs">Save ({reqConfig?.required.length} required)</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Student detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="student-achievements-dialog">
          <DialogHeader><DialogTitle className="font-display">{detail?.student_name} — Achievements</DialogTitle></DialogHeader>
          {detail && (
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span>{detail.stats.completed}/{detail.stats.required} required labs · {detail.stats.badges_earned} badges</span>
                <span className="font-mono">{detail.stats.pct}%</span>
              </div>
              <Progress value={detail.stats.pct} className="h-2 mb-4" />
              <div className={`rounded-lg border p-3 flex items-center justify-between mb-4 ${detail.certificate.status === "Awarded" ? "border-success/40 bg-success/5" : "border-border"}`}>
                <div className="flex items-center gap-2 text-sm">
                  {detail.certificate.status === "Awarded" ? <Award className="h-4 w-4 text-success" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                  <span>Certificate: <strong>{detail.certificate.status}</strong>{detail.certificate.certificate_id ? ` · ${detail.certificate.certificate_id}` : ""}</span>
                </div>
                {detail.certificate.status === "Awarded" && <Button size="sm" variant="outline" className="gap-1" onClick={reissueCert} data-testid="reissue-cert-btn"><RefreshCw className="h-3 w-3" /> Reissue</Button>}
              </div>
              <div className="max-h-72 overflow-auto space-y-1">
                {detail.labs.map((l) => (
                  <div key={l.id} className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm" data-testid={`aa-lab-${l.id}`}>
                    <div className="flex items-center gap-2">
                      {l.completed ? <CheckCircle2 className="h-4 w-4 text-success" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                      <span>{l.badge_title}</span>
                      {!l.required && <span className="text-[10px] uppercase text-muted-foreground">optional</span>}
                    </div>
                    {l.completed ? (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-muted-foreground">{l.badge_id}</span>
                        <Button size="sm" variant="ghost" className="gap-1 h-7" onClick={() => reissueBadge(l.id)} data-testid={`reissue-badge-${l.id}`}><RefreshCw className="h-3 w-3" /></Button>
                      </div>
                    ) : <span className="text-xs text-muted-foreground">not earned</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
